import React, { useState } from "react";

/**
 * InteractiveAreaModel
 * Props:
 *   p, q, r, s: coefficients for (p·x + q)(r·x + s)
 *   unit: pixel size per “1” (default 40)
 *   scale: overall zoom (default 1)
 *
 * Renders each block with just its algebraic term, and shows
 * the full multiplication description on hover below.
 */
export default function InteractiveAreaModel({
  p = 1,
  q = 3,
  r = 1,
  s = -2,
  unit = 40,
  scale = 1,
}) {
  const [hovered, setHovered] = useState(null);

  const u = unit * scale;

  // Precompute each block’s geometry, its short term, and full label
  const blocks = [
    {
      id: "b1",
      term: `${p * r}x²`,
      desc: `${p}·x × ${r}·x = ${p * r}x²`,
      x: 0,
      y: 0,
      w: u * Math.abs(r),
      h: u * Math.abs(p),
      color: "#e0f7fa",
    },
    {
      id: "b2",
      term: `${p * s}x`,
      desc: `${p}·x × ${s} = ${p * s}x`,
      x: u * Math.abs(r),
      y: 0,
      w: u * Math.abs(s),
      h: u * Math.abs(p),
      color: "#ffe082",
    },
    {
      id: "b3",
      term: `${q * r}x`,
      desc: `${q} × ${r}·x = ${q * r}x`,
      x: 0,
      y: u * Math.abs(p),
      w: u * Math.abs(r),
      h: u * Math.abs(q),
      color: "#a5d6a7",
    },
    {
      id: "b4",
      term: `${q * s}`,
      desc: `${q} × ${s} = ${q * s}`,
      x: u * Math.abs(r),
      y: u * Math.abs(p),
      w: u * Math.abs(s),
      h: u * Math.abs(q),
      color: "#ef9a9a",
    },
  ];

  const svgW = u * (Math.abs(r) + Math.abs(s));
  const svgH = u * (Math.abs(p) + Math.abs(q));

  return (
    <div style={{ textAlign: "center", margin: "1em 0" }}>
      <svg width={svgW} height={svgH + 24} style={{ border: "1px solid #ccc" }}>
        {blocks.map((b) => (
          <g key={b.id}>
            <rect
              x={b.x}
              y={b.y}
              width={b.w}
              height={b.h}
              fill={hovered === b.id ? "#ffcc80" : b.color}
              stroke="#555"
              onMouseEnter={() => setHovered(b.id)}
              onMouseLeave={() => setHovered(null)}
            />
            <text
              x={b.x + b.w / 2}
              y={b.y + b.h / 2 + 4}
              fontSize={16 * scale}
              fill="#333"
              textAnchor="middle"
            >
              {b.term}
            </text>
          </g>
        ))}

        {/* full description on hover */}
        {hovered && (
          <text
            x={svgW / 2}
            y={svgH + 18}
            fontSize={14 * scale}
            fill="#000"
            textAnchor="middle"
          >
            {blocks.find((b) => b.id === hovered).desc}
          </text>
        )}
      </svg>
      <p style={{ fontStyle: "italic", fontSize: 12 * scale }}>
        Hover over a block to see its full multiplication.
      </p>
    </div>
  );
}
